/**
 * Loads surah content from static JSON under public/surah-data/.
 * Merges core ayahs with per-language translation/tafsir maps to match the
 * former /api/surah/:id response shape.
 *
 * Words: loads /wbw-chunks/surah-N.json once, remaps into Map(ayah → words[]),
 * and serves the former /api/words response shape from that Map.
 */

const DATA_PREFIX = '/surah-data';
const WBW_PREFIX = '/wbw-chunks';

/** @type {Map<number, object>} */
const wbwSurahPayloadCache = new Map();

/**
 * In-flight WBW fetches per surah so parallel ayah loads (same surah) share one
 * network request instead of N identical fetches.
 * @type {Map<number, Promise<object>>}
 */
const wbwSurahInflight = new Map();

/**
 * Remapped ayah → words[] per surah (built once after payload load).
 * @type {Map<number, Map<number, object[]>>}
 */
const wbwWordsMapCache = new Map();

/**
 * In-flight Map builders (share remap work across parallel callers).
 * @type {Map<number, Promise<Map<number, object[]>>>}
 */
const wbwWordsMapInflight = new Map();

const LANG_FILES = [
  'gujarati',
  'english',
  'urdu',
  'hindi',
  'as_kalam_roman',
];

function surahDataUrl(surahId, suffix) {
  return `${DATA_PREFIX}/surah-${surahId}.${suffix}.json`;
}

async function fetchJson(url, signal) {
  const res = await fetch(url, { signal });
  if (!res.ok) {
    throw new Error(`Failed to load ${url}: ${res.status}`);
  }
  return res.json();
}

async function getWbwSurahPayload(surahId, signal) {
  const hit = wbwSurahPayloadCache.get(surahId);
  if (hit) return hit;

  let inflight = wbwSurahInflight.get(surahId);
  if (!inflight) {
    inflight = fetchJson(`${WBW_PREFIX}/surah-${surahId}.json`, signal)
      .then((payload) => {
        wbwSurahPayloadCache.set(surahId, payload);
        wbwSurahInflight.delete(surahId);
        return payload;
      })
      .catch((err) => {
        wbwSurahInflight.delete(surahId);
        throw err;
      });
    wbwSurahInflight.set(surahId, inflight);
  }
  return inflight;
}

function remapAyahWords(surahId, ayahNumber, rawWords) {
  const s = Number(surahId);
  const a = Number(ayahNumber);
  const list = Array.isArray(rawWords) ? rawWords : [];
  return list.map((w, idx) => {
    const loc = w.location || `${s}:${a}:${w.word ?? idx + 1}`;
    return {
      word_key: w.word_key || loc,
      surah: s,
      ayah: a,
      text: w.text,
      location: w.location || loc,
      word_order: w.word ?? idx + 1,
    };
  });
}

/**
 * Load one surah WBW file and return Map(ayahNumber → words[]).
 * Shared by warm preload and per-ayah lookups — remap runs once per surah.
 * @param {number|string} surahId
 * @param {AbortSignal} [signal]
 * @returns {Promise<Map<number, object[]>>}
 */
export async function loadWordsMapForSurah(surahId, signal) {
  const s = Number(surahId);
  if (!Number.isFinite(s)) {
    throw new Error('loadWordsMapForSurah requires numeric surahId');
  }

  const cached = wbwWordsMapCache.get(s);
  if (cached) return cached;

  let inflight = wbwWordsMapInflight.get(s);
  if (!inflight) {
    inflight = getWbwSurahPayload(s, signal)
      .then((payload) => {
        const map = new Map();
        for (const row of payload.ayahs || []) {
          const ayahNum = Number(row.ayah);
          if (!Number.isFinite(ayahNum)) continue;
          map.set(ayahNum, remapAyahWords(s, ayahNum, row.words));
        }
        wbwWordsMapCache.set(s, map);
        wbwWordsMapInflight.delete(s);
        return map;
      })
      .catch((err) => {
        wbwWordsMapInflight.delete(s);
        throw err;
      });
    wbwWordsMapInflight.set(s, inflight);
  }
  return inflight;
}

/**
 * @param {number|string} surahId
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ surah: object, ayahs: object[] }>}
 */
export async function loadSurahMerged(surahId, signal) {
  const id = Number(surahId);
  const urls = [surahDataUrl(id, 'core'), ...LANG_FILES.map((lang) => surahDataUrl(id, lang))];
  const parts = await Promise.all(urls.map((u) => fetchJson(u, signal)));

  const core = parts[0];
  const langMaps = parts.slice(1);

  const ayahs = core.ayahs.map((ayah) => {
    const merged = { ...ayah };
    for (const map of langMaps) {
      const extra = map[ayah.ayah_id] || {};
      Object.assign(merged, extra);
    }
    return merged;
  });

  return { surah: core.surah, ayahs };
}

/**
 * @param {number|string} surahId
 * @param {AbortSignal} [signal]
 */
export async function loadRukus(surahId, signal) {
  const id = Number(surahId);
  return fetchJson(`${DATA_PREFIX}/rukus-${id}.json`, signal);
}

/**
 * @param {AbortSignal} [signal]
 */
export async function loadSurahsIndex(signal) {
  return fetchJson(`${DATA_PREFIX}/surahs-index.json`, signal);
}

/**
 * Same shape as GET /api/words?surah=&ayah=
 * @param {number|string} surah
 * @param {number|string} ayah
 * @param {AbortSignal} [signal]
 */
export async function loadWordsForAyah(surah, ayah, signal) {
  const s = Number(surah);
  const a = Number(ayah);
  if (!Number.isFinite(s) || !Number.isFinite(a)) {
    throw new Error('loadWordsForAyah requires numeric surah and ayah');
  }

  const map = await loadWordsMapForSurah(s, signal);
  const words = map.get(a) || [];

  return {
    words,
    count: words.length,
    surah: s,
    ayah: a,
  };
}

/**
 * Same shape as GET /api/parahs
 * @param {AbortSignal} [signal]
 */
export async function loadParahs(signal) {
  return fetchJson(`${DATA_PREFIX}/parahs.json`, signal);
}

/**
 * Same shape as GET /api/sajdas
 * @param {AbortSignal} [signal]
 */
export async function loadSajdas(signal) {
  return fetchJson(`${DATA_PREFIX}/sajdas.json`, signal);
}

/**
 * Same shape as GET /api/pages?page= — array of line rows
 * @param {number|string} pageNumber
 * @param {AbortSignal} [signal]
 */
export async function loadPageLines(pageNumber, signal) {
  const n = Number(pageNumber);
  if (!Number.isFinite(n) || n < 1) {
    throw new Error('loadPageLines requires page >= 1');
  }
  return fetchJson(`${DATA_PREFIX}/pages/${n}.json`, signal);
}
